import { useEffect, useState } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import {
  ShoppingCart, CheckCircle2, RefreshCw, Trash2, Loader2, Receipt, Package,
  Plus, FileText, Upload, FileDown, Search,
} from 'lucide-react';
import OrderDocumentEditor from './OrderDocumentEditor';
import MaterialCheckDialog from './MaterialCheckDialog';
import OrderHistoryModal from '../../components/OrderHistoryModal';
import OrderTasksChecklistPanel from './OrderTasksChecklistPanel';
import { localYMD } from '../../lib/dates';
import { toast } from 'sonner';

// Highlight-Zeitraum für „aktiver Auftrag" (nach Aktivität): 12 Stunden.
const ACTIVITY_HIGHLIGHT_MS = 12 * 3600 * 1000;

// Rote Aktivitäts-Punkte erklärbar machen: aus dem letzten Aktivitäts-
// Ereignis einen lesbaren Tooltip bauen (Was / Wer / Wann).
const ACTIVITY_KIND_LABEL = {
  task_new: 'Neue Aufgabe',
  task_comment: 'Neuer Kommentar zur Aufgabe',
  follow_up: 'Neuer Kommentar / Follow-up',
  comment: 'Neuer Kommentar',
  task: 'Neue Aufgabe',
  status: 'Statuswechsel',
  status_change: 'Statuswechsel',
  checklist: 'Checklisten-Änderung',
  checklist_next: 'Checklisten-Fortschritt',
  attachment: 'Neuer Anhang',
  picking: 'Kommissionierung',
  inventory_booked: 'Lagerbuchung',
  offer: 'Angebot eingegangen',
  activity: 'Neue Aktivität',
};
function unreadActivityTitle(info) {
  if (!info) return 'Neue/ungelesene Aufgabe — jetzt öffnen';
  const what = ACTIVITY_KIND_LABEL[info.kind] || 'Neue Aufgabe';
  const t = info.title ? `: „${info.title}"` : '';
  const extra = info.unread > 0 ? ` · ${info.unread} ungelesen` : '';
  return `${what}${t}${extra}`;
}


const COLUMNS = [
  { id: 'new', label: 'Neu', color: 'bg-blue-50 border-blue-200' },
  { id: 'in_progress', label: 'In Bearbeitung', color: 'bg-amber-50 border-amber-200' },
  { id: 'production', label: 'Produktion', color: 'bg-purple-50 border-purple-200' },
  // 28.02.2026 (User-Wunsch): Neue Spalte „Versand" rechts der Spalte
  // „Produktion". Auto-Move via `SECTION_TO_STATUS` im Backend
  // (Produktion & Fertigstellung → shipping; Versand & Abschluss → done).
  { id: 'shipping', label: 'Versand', color: 'bg-orange-50 border-orange-200' },
  { id: 'done', label: 'Erledigt', color: 'bg-emerald-50 border-emerald-200' },
];

export default function OrdersPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const canDelete = can('orders.delete');
  const isProduktion = user?.role === 'produktion';
  // 03.09.2026: Preise nur bei entsprechender Berechtigung anzeigen —
  // Technik (Alexander Schwabe, Thomas Fasel) und Produktion sehen
  // sie NICHT (siehe permission_actions.py `orders.view_prices`).
  // 04.02.2026: hardcoded `user.role === 'admin'`-Kurzschluss entfernt —
  // Admin bekommt Wildcard `*` über usePermissions und ist damit automatisch
  // abgedeckt. Nutze ausschließlich die Matrix-Aktion.
  const canSeePrices = can('orders.view_prices');
  // 28.02.2026 Matrix-Cleanup Runde 2: Sichtbarkeit hängt jetzt am
  // Registry-Key `orders.import` (statt hardcoded Rollen-Liste). Auch
  // die Rolle „Team" bekommt so ihre Rechte laut Matrix — nicht mehr
  // durch Namens-Zufall.
  const canImport = can('orders.import');
  const canStatus = can('orders.status');
  const [buckets, setBuckets] = useState({ new: [], in_progress: [], production: [], shipping: [], done: [] });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [selected, setSelected] = useState(new Set());  // Set of order-ids
  const [deleting, setDeleting] = useState(false);
  const [importNumber, setImportNumber] = useState('');
  const [importing, setImporting] = useState(false);
  const [excelImporting, setExcelImporting] = useState(false);
  const [docOrderId, setDocOrderId] = useState(null);
  const [materialOrderId, setMaterialOrderId] = useState(null);
  const [historyOrderId, setHistoryOrderId] = useState(null);
  const [historyInitialTab, setHistoryInitialTab] = useState(null);
  const [checklistOrder, setChecklistOrder] = useState(null);
  const [unreadOrderIds, setUnreadOrderIds] = useState(() => new Set());
  const [unreadDetails, setUnreadDetails] = useState({});
  const [preview, setPreview] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // 01.09.2026: EES-Speichersystem-Seriennummer (fortlaufend). Wird oben
  // im Aufträge-Tab angezeigt („zuletzt: 10000305 · Auftrag 11922").
  const [eesSerial, setEesSerial] = useState(null);
  const loadEesSerial = async () => {
    const r = await authFetch('/api/orders/ees-serial/last');
    if (r.ok) setEesSerial(await r.json());
  };
  useEffect(() => { loadEesSerial(); }, []);

  // Volltext-Suche über Auftragsnummer, Kunde, Kommission UND Seriennummern.
  // Debounce 350 ms. Bei leerer Suche → wieder alle Aufträge.
  useEffect(() => {
    const q = searchQuery.trim();
    const t = setTimeout(() => { load(q); }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const q = importNumber.trim();
    if (q.length < 3) { setPreview(null); return; }
    const t = setTimeout(async () => {
      const r = await authFetch(`/api/integrations/easybill/orders/preview?number=${encodeURIComponent(q)}`);
      if (r.ok) setPreview(await r.json()); else setPreview({ found: false });
    }, 400);
    return () => clearTimeout(t);
  }, [importNumber]);

  const importExcel = async (file) => {
    if (!file) return;
    setExcelImporting(true); setMsg(null); setErr(null);
    const fd = new FormData();
    fd.append('file', file);
    const res = await authFetch('/api/integrations/easybill/orders/import-excel', {
      method: 'POST', body: fd,
    });
    setExcelImporting(false);
    if (res.ok) {
      const d = await res.json();
      setMsg(`Excel-Import abgeschlossen: ${d.inserted} neu, ${d.updated} aktualisiert (${d.kind === 'invoices' ? 'Rechnungen' : 'Aufträge'}).`);
      load();
    } else {
      const d = await res.json().catch(() => ({}));
      setErr(d.detail || 'Excel-Import fehlgeschlagen');
    }
  };

  const load = async (qOverride) => {
    setLoading(true); setErr(null);
    const q = (qOverride ?? searchQuery ?? '').trim();
    const url = q ? `/api/integrations/easybill/orders?q=${encodeURIComponent(q)}` : '/api/integrations/easybill/orders';
    const res = await authFetch(url);
    setLoading(false);
    if (res.ok) {
      const d = await res.json();
      setBuckets(d.buckets || { new: [], in_progress: [], production: [], shipping: [], done: [] });
      setTotal(d.total || 0);
      loadEesSerial();  // Banner nach Auftrags-Refresh aktualisieren
      // Drop any selection that's no longer loaded
      setSelected(prev => {
        const knownIds = new Set((d.orders || []).map(o => o.id));
        const next = new Set();
        for (const id of prev) if (knownIds.has(id)) next.add(id);
        return next;
      });
    } else {
      const d = await res.json().catch(() => ({}));
      setErr(d.detail || 'Aufträge konnten nicht geladen werden');
    }
    // Rote Punkte NUR bei einer für mich neuen/ungelesenen Aufgabe zum Auftrag
    // (User-Wunsch 27.10.2026). Quelle: Aufgaben-System, nicht generische
    // „Aktivität seit letztem Öffnen".
    const uRes = await authFetch(`/api/tasks/order-alerts`);
    if (uRes.ok) {
      const ud = await uRes.json();
      setUnreadOrderIds(new Set(ud.orderIds || []));
      setUnreadDetails(ud.details || {});
    }
  };

  useEffect(() => { load(); }, []);

  // Deep-Link Support: `?open=<orderId>` ODER `?order=<orderId>&tab=<tabKey>`
  // öffnet OrderHistoryModal direkt. `?order=…&tab=…` wird z. B. von der
  // Dashboard-NotificationsInbox genutzt, damit ein Klick auf eine
  // Auftrags-Aufgabe direkt den Tasks-Tab öffnet. `?open=…` (Altform) bleibt
  // aus Rückwärtskompatibilität mit der TasksPage bestehen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('order') || params.get('open');
    const tabKey = params.get('tab');
    if (openId) {
      setHistoryOrderId(openId);
      if (tabKey) setHistoryInitialTab(tabKey);
      // Query-Params wieder entfernen — sonst öffnet er sich beim nächsten
      // Reload immer wieder.
      const url = new URL(window.location.href);
      url.searchParams.delete('open');
      url.searchParams.delete('order');
      url.searchParams.delete('tab');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Bulk-Refresh aller Easybill-Entwürfe alle 60 Sekunden — aber nur wenn
  // die Seite gerade sichtbar ist. Sobald Easybill für einen Entwurf eine
  // Auftragsnummer vergibt (z. B. nach Freigabe im Easybill-UI), wird sie
  // automatisch in die DB übernommen und die Liste neu geladen.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      try {
        const r = await authFetch('/api/integrations/easybill/orders/refresh-drafts', { method: 'POST' });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled && d.refreshed && d.refreshed.length > 0) {
          // Pro neu eingelesener Auftragsnummer ein dezenter Toast — der
          // Innendienst sieht so direkt, dass Easybill freigegeben wurde.
          for (const entry of d.refreshed) {
            toast.success(`Neue Auftragsnummer eingegangen: ${entry.easybillDocumentNumber}`, {
              duration: 6000,
              id: `eb-refresh-${entry.orderId}`,  // dedupliziert bei Mehrfach-Treffern
            });
          }
          await load();
        }
      } catch {
        // still leise — Polling soll niemals UI-Fehler werfen
      }
    };
    const id = setInterval(tick, 60_000);
    // Beim Tab-Reaktivieren sofort einen Sweep starten
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const importOne = async () => {
    if (!importNumber.trim()) { setErr('Bitte Auftrags- oder Rechnungsnummer eingeben'); return; }
    setImporting(true); setErr(null); setMsg(null);
    const res = await authFetch('/api/integrations/easybill/orders/import', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ easybillDocumentNumber: importNumber.trim() }),
    });
    setImporting(false);
    if (res.ok) {
      const d = await res.json();
      setMsg(d.imported ? `Importiert: ${d.order.easybillDocumentNumber}` : `Bereits vorhanden (aktualisiert): ${d.order.easybillDocumentNumber}`);
      setImportNumber('');
      await load();
    } else {
      const d = await res.json().catch(() => ({}));
      setErr(d.detail || 'Import fehlgeschlagen');
    }
  };

  const setStatus = async (orderId, status) => {
    const res = await authFetch(`/api/integrations/easybill/orders/${orderId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (res.ok) load();
    else {
      const d = await res.json().catch(() => ({}));
      const msg = d.detail || 'Status-Update fehlgeschlagen';
      // Etappe A: Fertigstellung ohne komplette Checkliste → freundlicher
      // Hinweis statt Error-Banner
      if (status === 'done' && res.status === 400) toast.error(msg);
      else setErr(msg);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectColumn = (colId, checked) => {
    setSelected(prev => {
      const next = new Set(prev);
      for (const o of buckets[colId] || []) {
        if (checked) next.add(o.id);
        else next.delete(o.id);
      }
      return next;
    });
  };

  const selectAll = (checked) => {
    if (!checked) { setSelected(new Set()); return; }
    const all = new Set();
    for (const col of COLUMNS) for (const o of buckets[col.id] || []) all.add(o.id);
    setSelected(all);
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} Aufträge aus dem Dashboard entfernen? Easybill bleibt unberührt.`)) return;
    setDeleting(true);
    const res = await authFetch('/api/integrations/easybill/orders/bulk-delete', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: Array.from(selected) }),
    });
    setDeleting(false);
    if (res.ok) {
      const d = await res.json();
      setMsg(`${d.deleted} Aufträge gelöscht.`);
      setSelected(new Set());
      load();
    } else {
      const d = await res.json().catch(() => ({}));
      setErr(d.detail || 'Löschen fehlgeschlagen');
    }
  };

  const allCount = (buckets.new?.length || 0) + (buckets.in_progress?.length || 0)
                 + (buckets.production?.length || 0) + (buckets.shipping?.length || 0)
                 + (buckets.done?.length || 0);
  const allSelected = allCount > 0 && selected.size === allCount;

  return (
    <div className="p-6 h-full flex flex-col bg-[#f5f6f7]" data-testid="orders-page">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-[#32373c] flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#9ec043]" /> Aufträge
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Lokale Kanban-Spiegelung. Importiere gezielt Aufträge / Rechnungen aus Easybill.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {msg && <span className="text-xs text-[#7ca735] bg-[#9ec043]/10 border border-[#9ec043]/30 rounded px-2 py-1">{msg}</span>}
          {/* Volltext-Suche: Auftragsnr, Kunde, Kommission, Seriennummern */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Suche: Auftrag, Kunde, Seriennummer …"
              className="h-9 text-xs w-[260px] pl-7"
              data-testid="orders-search-input"
            />
          </div>
          <div className="flex items-center gap-1" data-testid="orders-import-bar">
            {canImport && <>
            <Input value={importNumber}
                   onChange={e => setImportNumber(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && importOne()}
                   placeholder="Easybill-Nummer (z.B. AU-2026-0001)"
                   className="h-9 text-xs w-[220px]"
                   data-testid="orders-import-input" />
            <Button onClick={importOne} disabled={importing || !importNumber.trim()}
                    className="h-9 text-xs bg-[#9ec043] hover:bg-[#8aad36] text-white"
                    data-testid="btn-orders-import">
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" />Import</>}
            </Button>
            </>}
          </div>
          {canImport && (
          <label className="h-9 inline-flex items-center gap-1 text-xs border border-gray-200 hover:border-[#9ec043] hover:text-[#9ec043] rounded px-3 cursor-pointer transition-colors" data-testid="btn-orders-excel">
            {excelImporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Excel (Auftrags-/Rechnungsbuch)
            <input type="file" accept=".xlsx,.xls" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; importExcel(f); e.target.value = ''; }} />
          </label>
          )}
          <Button variant="outline" onClick={load} disabled={loading} className="h-9 text-xs" data-testid="btn-orders-reload">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" />Neu laden</>}
          </Button>
        </div>
      </div>

      {canImport && <DateRangeImportBar onImported={(res) => { setMsg(`${res.importedCount} importiert · ${res.skippedCount} bereits vorhanden · ${res.failedCount} Fehler`); load(); }} />}

      {err && <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="orders-error">{err}</div>}

      {/* 01.09.2026: EES-Speichersystem-Seriennummern-Anzeige — immer
          sichtbar, auch wenn noch keine Seriennummer vergeben wurde. */}
      {eesSerial && (
        <div className="mb-3 flex items-center gap-3 text-xs bg-[#9ec043]/10 border border-[#9ec043]/40 text-[#32373c] rounded-lg px-3 py-1.5"
             data-testid="ees-serial-banner">
          <span className="text-[10px] uppercase tracking-wide text-[#7ca735] font-semibold">EES-Seriennummer</span>
          {eesSerial.lastSerial ? (
            <>
              <span className="text-gray-500">Zuletzt:</span>
              <span className="font-mono font-bold text-[#7ca735]">{eesSerial.lastSerial}</span>
              {eesSerial.lastOrderNumber && (
                <span className="text-gray-500">→ Auftrag {eesSerial.lastOrderNumber}</span>
              )}
              {eesSerial.lastCustomer && (
                <span className="text-gray-500 truncate max-w-[240px]">· {eesSerial.lastCustomer}</span>
              )}
            </>
          ) : (
            <span className="text-gray-500 italic">Noch keine vergeben</span>
          )}
          <span className="ml-auto text-gray-500">
            Nächste: <span className="font-mono font-bold text-[#7ca735]">{eesSerial.nextSerial}</span>
          </span>
        </div>
      )}

      {preview && importNumber.trim().length >= 3 && (
        <div className={`mb-3 text-xs rounded-lg border px-3 py-2 flex items-center gap-3 ${preview.found ? 'bg-[#9ec043]/5 border-[#9ec043]/30 text-[#32373c]' : 'bg-gray-50 border-gray-200 text-gray-400'}`} data-testid="orders-import-preview">
          {preview.found ? (
            <>
              <span className="inline-flex items-center gap-1 text-[#9ec043] font-medium">✓ Gefunden:</span>
              <span className="font-semibold">{preview.type === 'INVOICE' ? 'Rechnung' : 'Auftrag'} {preview.documentNumber}</span>
              <span>·</span>
              <span>{preview.customerName || '—'}</span>
              {preview.title && <><span>·</span><span className="truncate max-w-xs" title={preview.title}>{preview.title}</span></>}
              {preview.grandTotalGross != null && <><span>·</span><span className="tabular-nums font-semibold">{Number(preview.grandTotalGross).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span></>}
            </>
          ) : (
            <span>Keine Übereinstimmung in Easybill</span>
          )}
        </div>
      )}

      {/* Bulk-action bar — sichtbar mit Recht `orders.delete` (Admin + Innendienst) */}
      {canDelete && (
        <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2 mb-3 text-xs"
             data-testid="orders-bulk-bar">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox checked={allSelected} onCheckedChange={v => selectAll(!!v)}
                      data-testid="orders-select-all" />
            <span className="text-gray-600">Alle markieren</span>
          </label>
          <span className="text-gray-400">|</span>
          <span className="text-gray-600" data-testid="orders-selected-count">
            {selected.size} markiert
          </span>
          <Button onClick={bulkDelete} disabled={selected.size === 0 || deleting}
                  variant="outline"
                  className="ml-auto h-7 text-xs text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40"
                  data-testid="btn-orders-bulk-delete">
            {deleting && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            <Trash2 className="w-3 h-3 mr-1" /> Markierte löschen
          </Button>
        </div>
      )}

      {/* 4 aktive Workflow-Spalten nebeneinander (Neu / In Bearbeitung /
          Produktion / Versand). Erledigt bekommt eine eigene, volle Zeile
          darunter — 28.02.2026 User-Wunsch. */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 min-h-0 overflow-hidden">
        {COLUMNS.filter(c => c.id !== 'done').map(col => (
          <KanbanColumn
            key={col.id}
            column={col}
            orders={buckets[col.id] || []}
            setStatus={setStatus}
            canDelete={canDelete}
            isProduktion={isProduktion}
            canSeePrices={canSeePrices}
            selected={selected}
            toggleSelect={toggleSelect}
            selectColumn={selectColumn}
            onOpenDoc={setDocOrderId}
            onOpenMaterial={setMaterialOrderId}
            onOpenHistory={(id) => { setHistoryOrderId(id); setUnreadOrderIds(prev => { const n = new Set(prev); n.delete(id); return n; }); }}
            onOpenChecklist={setChecklistOrder}
            unreadOrderIds={unreadOrderIds}
            unreadDetails={unreadDetails}
          />
        ))}
      </div>

      {/* Erledigt-Spalte: volle Breite unter den 4 Workflow-Spalten */}
      <div className="mt-3">
        {(() => {
          const doneCol = COLUMNS.find(c => c.id === 'done');
          return doneCol ? (
            <KanbanColumn
              column={doneCol}
              orders={buckets.done || []}
              setStatus={setStatus}
              canDelete={canDelete}
              isProduktion={isProduktion}
              canSeePrices={canSeePrices}
              selected={selected}
              toggleSelect={toggleSelect}
              selectColumn={selectColumn}
              onOpenDoc={setDocOrderId}
              onOpenMaterial={setMaterialOrderId}
              onOpenHistory={(id) => { setHistoryOrderId(id); setUnreadOrderIds(prev => { const n = new Set(prev); n.delete(id); return n; }); }}
              onOpenChecklist={setChecklistOrder}
              unreadOrderIds={unreadOrderIds}
              unreadDetails={unreadDetails}
            />
          ) : null;
        })()}
      </div>

      <div className="text-[11px] text-gray-400 mt-2 text-right">{total} Aufträge insgesamt</div>

      {docOrderId && <OrderDocumentEditor orderId={docOrderId} onClose={() => setDocOrderId(null)} />}
      {materialOrderId && <MaterialCheckDialog orderId={materialOrderId} onClose={() => setMaterialOrderId(null)} />}
      {historyOrderId && <OrderHistoryModal orderId={historyOrderId} initialTab={historyInitialTab} onClose={() => {
        setHistoryOrderId(null); setHistoryInitialTab(null);
        // Nach dem Schließen die Aufgaben-Punkte neu laden — das Modal hat beim
        // Öffnen den Lese-Cursor gesetzt, der Punkt soll jetzt dauerhaft weg sein.
        authFetch(`/api/tasks/order-alerts`).then(r => r.ok && r.json().then(ud => {
          setUnreadOrderIds(new Set(ud.orderIds || [])); setUnreadDetails(ud.details || {});
        }));
      }} onReimported={load} />}
      {checklistOrder && <OrderTasksChecklistPanel order={checklistOrder} onClose={() => setChecklistOrder(null)} onChanged={load} />}
    </div>
  );
}

function DateRangeImportBar({ onImported }) {
  const today = localYMD(new Date());
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [importing, setImporting] = useState(false);

  const preset = (key) => {
    const d = new Date();
    if (key === 'today') {
      setFromDate(localYMD(d));
      setToDate(localYMD(d));
    } else if (key === 'yesterday') {
      const y = new Date(d.getTime() - 86400000);
      setFromDate(localYMD(y));
      setToDate(localYMD(y));
    } else if (key === '7') {
      const d7 = new Date(d.getTime() - 7 * 86400000);
      setFromDate(localYMD(d7));
      setToDate(localYMD(d));
    } else if (key === '30') {
      const d30 = new Date(d.getTime() - 30 * 86400000);
      setFromDate(localYMD(d30));
      setToDate(localYMD(d));
    }
  };

  const run = async () => {
    setImporting(true);
    const r = await authFetch('/api/integrations/easybill/orders/import-by-date', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fromDate, toDate }),
    });
    setImporting(false);
    if (r.ok) {
      const res = await r.json();
      onImported?.(res);
    } else {
      const d = await r.json().catch(() => ({}));
      alert(d.detail || 'Import fehlgeschlagen');
    }
  };

  return (
    <div className="mb-3 bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap text-xs" data-testid="orders-date-range-bar">
      <span className="text-gray-500 font-medium">Zeitraum importieren:</span>
      <button onClick={() => preset('today')} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" data-testid="btn-preset-today">heute</button>
      <button onClick={() => preset('yesterday')} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" data-testid="btn-preset-yesterday">gestern</button>
      <button onClick={() => preset('7')} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" data-testid="btn-preset-7">7 Tage</button>
      <button onClick={() => preset('30')} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" data-testid="btn-preset-30">30 Tage</button>
      <span className="text-gray-300">|</span>
      <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="px-2 py-1 border border-gray-200 rounded" data-testid="input-date-from" />
      <span className="text-gray-400">bis</span>
      <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="px-2 py-1 border border-gray-200 rounded" data-testid="input-date-to" />
      <button onClick={run} disabled={importing}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-[#9ec043] text-white hover:bg-[#7ca735] disabled:opacity-50 font-medium"
        data-testid="btn-import-date-range">
        {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
        Importieren
      </button>
    </div>
  );
}

function KanbanColumn({ column, orders, setStatus, canDelete, isProduktion, canSeePrices, selected, toggleSelect, selectColumn, onOpenDoc, onOpenMaterial, onOpenHistory, onOpenChecklist, unreadOrderIds, unreadDetails }) {
  const colAllSelected = orders.length > 0 && orders.every(o => selected.has(o.id));
  // 28.02.2026 (User-Wunsch): „Erledigt" ist eine volle Zeile unter den
  // 4 Workflow-Spalten — Cards horizontal wrappen statt vertikal stapeln.
  const isDoneRow = column.id === 'done';
  return (
    <div className={`rounded-xl border ${column.color} flex flex-col min-h-0`}
         data-testid={`orders-col-${column.id}`}>
      <div className="p-3 border-b border-black/5 flex items-center gap-2">
        {canDelete && orders.length > 0 && (
          <Checkbox checked={colAllSelected}
                    onCheckedChange={v => selectColumn(column.id, !!v)}
                    data-testid={`orders-col-select-${column.id}`} />
        )}
        <h3 className="text-sm font-semibold text-[#32373c]">{column.label}</h3>
        <span className="text-[11px] text-gray-500 bg-white border border-gray-200 rounded-full px-2 py-0.5 ml-auto">
          {orders.length}
        </span>
      </div>
      <div className={
        isDoneRow
          ? "flex-1 overflow-y-auto p-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 max-h-56"
          : "flex-1 overflow-y-auto p-2 space-y-2"
      }>
        {orders.length === 0 && (
          <div className={isDoneRow
            ? "text-[11px] text-gray-400 text-center py-4 col-span-full"
            : "text-[11px] text-gray-400 text-center py-8"}>Keine Einträge</div>
        )}
        {orders.map(o => (
          <OrderCard key={o.id} order={o} setStatus={setStatus} canDelete={canDelete}
                     isProduktion={isProduktion}
                     canSeePrices={canSeePrices}
                     isSelected={selected.has(o.id)} onSelect={toggleSelect}
                     onOpenDoc={onOpenDoc}
                     onOpenMaterial={onOpenMaterial}
                     onOpenHistory={onOpenHistory}
                     onOpenChecklist={onOpenChecklist}
                     hasUnread={unreadOrderIds?.has(o.id)}
                     unreadInfo={unreadDetails?.[o.id]} />
        ))}
      </div>
    </div>
  );
}

function OrderCard({ order, setStatus, canDelete, isProduktion, canSeePrices, isSelected, onSelect, onOpenDoc, onOpenMaterial, onOpenHistory, onOpenChecklist, hasUnread, unreadInfo }) {
  const isInvoice = order.easybillType === 'INVOICE';
  // 28.02.2026 Punkt-3-Runde-2: Bestellungen visuell klar von Aufträgen
  // trennen (orange Badge, Lieferschein-Icon, Bezeichnung „Bestellung").
  const isBestellung = order.orderKind === 'bestellung';
  const Icon = isBestellung ? FileText : (isInvoice ? Receipt : Package);
  const typeLabel = isBestellung ? 'Bestellung' : (isInvoice ? 'Rechnung' : 'Auftrag');
  const iconColor = isBestellung ? 'text-orange-600'
                    : (isInvoice ? 'text-emerald-600' : 'text-blue-600');
  const badgeStyle = isBestellung ? 'bg-orange-100 text-orange-700'
                    : (isInvoice ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700');

  // 28.02.2026: PDF-Button, Material-Button, Dokument-Button und die
  // Status-Wechsel-Buttons wurden aus der Kanban-Karte entfernt (User-
  // Wunsch für kompaktere Kanban-Übersicht). Alle Aktionen sind weiterhin
  // über das Detail-Modal (Klick auf Auftragsnummer) erreichbar.

  // Aktivitäts-Highlight: Auftrag hat in den letzten 12 h eine Aktivität
  // gesehen (Checklisten-Toggle, Kommentar, Anhang) → Karte hebt sich ab.
  const isActive = order.lastActivityAt
    && (Date.now() - new Date(order.lastActivityAt).getTime()) < ACTIVITY_HIGHLIGHT_MS;

  // Checklisten-Fortschritt (Etappe B, 27.02.2026)
  const checklist = order.checklist || [];
  const clDone = checklist.filter(it => it.done).length;
  const clTotal = checklist.length;
  const clComplete = clTotal > 0 && clDone === clTotal;
  // 01.09.2026 (User-Wunsch): nächster offener Checklisten-Punkt als eigene
  // Zeile unter der Checklisten-Zusammenfassung. Rolle als Badge rechts.
  const nextOpenItem = checklist.find(it => !it.done) || null;
  const ROLE_BADGE = {
    innendienst: { bg: '#8080c0', label: 'Innendienst' },
    lager:       { bg: '#ff8000', label: 'Lager' },
    produktion:  { bg: '#8B4513', label: 'Produktion' },
    technik:     { bg: '#0080ff', label: 'Technik' },
  };

  // 28.02.2026 (User-Wunsch): Erledigt-Spalte kompakt — nur AB-Nr, Kunde,
  // Kommission in EINER Zeile. Modal weiterhin per Klick auf die Karte.
  if (order.status === 'done') {
    return (
      <div
        onClick={() => onOpenHistory(order.id)}
        className="bg-white border border-gray-200 rounded-md px-2.5 py-1.5 hover:shadow-sm cursor-pointer transition"
        data-testid={`order-card-${order.id}`}
      >
        <div className="flex items-center gap-2 text-[11px] min-w-0">
          <span className="font-semibold text-[#32373c] whitespace-nowrap">
            {order.easybillDocumentNumber || '—'}
          </span>
          <span className="text-gray-400">·</span>
          <span className="text-gray-700 truncate flex-1">
            {order.customerCompany || order.customerName || '—'}
          </span>
          {(order.commission || order.projectName) && (
            <span className="text-gray-500 text-[10px] truncate max-w-[35%]">
              {order.commission || order.projectName}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border rounded-lg p-3 text-xs transition ${
      isSelected ? 'border-[#9ec043] ring-1 ring-[#9ec043]/40'
        : isActive ? 'border-amber-300 ring-1 ring-amber-200 shadow-sm'
        : 'border-gray-200 hover:shadow-sm'
    }`}
         data-testid={`order-card-${order.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          {canDelete && (
            <Checkbox checked={isSelected} onCheckedChange={() => onSelect(order.id)}
                      className="shrink-0"
                      data-testid={`order-card-select-${order.id}`} />
          )}
          <Icon className={`w-3.5 h-3.5 shrink-0 ${iconColor}`} />
          {hasUnread && (
            <span
              className="inline-flex w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0"
              title={unreadActivityTitle(unreadInfo)}
              data-testid={`order-bell-${order.id}`}
            />
          )}
          <button
            onClick={() => onOpenHistory?.(order.id)}
            className="font-semibold text-[#32373c] truncate hover:text-[#9ec043] text-left"
            title={
              order.easybillDocumentId && !order.easybillDocumentNumber
                ? 'Easybill-Entwurf — Nummer wird erst nach Freigabe in Easybill vergeben. „Aus Easybill aktualisieren" im Detail-Modal zieht sie nach.'
                : 'Auftragshistorie öffnen'
            }
            data-testid={`btn-order-history-${order.id}`}
          >
            {order.easybillDocumentNumber
              || (order.easybillDocumentId ? <span className="text-purple-600 italic">Entwurf</span> : '—')}
          </button>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${badgeStyle}`}>
            {typeLabel}
          </span>
        </div>
        {order.grandTotalGross != null && canSeePrices && (
          <span className="font-semibold text-[#32373c] shrink-0">
            {Number(order.grandTotalGross).toLocaleString('de-DE', { style: 'currency', currency: order.currency || 'EUR' })}
          </span>
        )}
      </div>

      <div className="text-gray-600 truncate mb-0.5">
        {order.customerCompany || order.customerName || '—'}
      </div>
      {order.title && (
        <div className="text-gray-500 truncate mb-1 italic">{order.title}</div>
      )}

      <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-1">
        {order.easybillDocumentDate && <span>{order.easybillDocumentDate}</span>}
        {(order.confirmedDeliveryDate || order.dueDate) && (
          <span className="text-[#32373c] font-medium" title="Bestätigter Liefertermin">
            · Liefertermin {order.confirmedDeliveryDate || order.dueDate}
          </span>
        )}
        {isActive && (
          <span className="text-amber-700 font-medium" title="Aktivität in den letzten 12 h">
            · aktiv
          </span>
        )}
        {order.easybillStatus && (
          <span className="uppercase bg-gray-100 px-1.5 py-0.5 rounded">
            {order.easybillStatus}
          </span>
        )}
        {/* 28.02.2026 (User-Wunsch): Kompaktere Karte — nur „Checkliste x/y"
            in der Fußzeile, rechtsbündig auf der Datums-Zeile. Material /
            Dokument / PDF / Status-Buttons sind weiterhin im Detail-Modal
            verfügbar (Klick auf die Auftragsnummer). */}
        {!isInvoice && (
          <button onClick={() => onOpenChecklist?.(order)}
                  className="ml-auto text-[10px] text-[#32373c] hover:text-[#9ec043] flex items-center gap-0.5 font-medium"
                  title="Aufgaben & Checkliste"
                  data-testid={`btn-order-checklist-${order.id}`}>
            <CheckCircle2 className={`w-3 h-3 ${clComplete ? 'text-emerald-600' : ''}`} />
            Checkliste
            {clTotal > 0 && (
              <span className={`ml-0.5 px-1 rounded text-[9px] font-mono ${
                clComplete ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
              }`}
                    data-testid={`checklist-progress-${order.id}`}>
                {clDone}/{clTotal}
              </span>
            )}
          </button>
        )}
      </div>

      {/* 01.09.2026 (User-Wunsch): Nächster offener Checklisten-Punkt in
          eigener Zeile — Label links, Rollen-Badge rechts. Zeigt sofort,
          wer als Nächstes was zu tun hat. */}
      {!isInvoice && nextOpenItem && (
        <div className="flex items-center gap-2 mt-1"
             data-testid={`next-open-${order.id}`}>
          <span className="text-[11px] text-[#32373c] truncate flex-1"
                title={nextOpenItem.label}>
            → {nextOpenItem.label}
          </span>
          {nextOpenItem.role && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full text-white font-medium tracking-wide shrink-0"
              style={{ backgroundColor: (ROLE_BADGE[nextOpenItem.role]?.bg) || '#6b7280' }}
              data-testid={`next-open-role-${order.id}`}
            >
              {ROLE_BADGE[nextOpenItem.role]?.label || nextOpenItem.role}
            </span>
          )}
        </div>
      )}

      {order.linkedInquiryId && (
        <div className="mt-1.5 text-[10px] text-gray-400 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          <span>Aus Angebot / Anfrage verknüpft</span>
        </div>
      )}
    </div>
  );
}
